/**
 * SectionCard (TMMPDM-0100): one content block on `background.paper` against the grey page.
 * Sibling spacing comes from the parent stack. This card has no outer margin.
 */
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useId, type ReactNode } from 'react';

export interface SectionCardProps {
  /** Optional heading. When set, the section is labelled by it. */
  title?: ReactNode;
  /** Default `h2`. Typography follows the theme's h2 / h3 variants. */
  headingLevel?: 'h2' | 'h3';
  /** body2, text.secondary, under the title. */
  subtitle?: ReactNode;
  /** Top-right; wraps under the title on a narrow width. */
  actions?: ReactNode;
  /** The heading id. Generated when omitted and a title is set. */
  id?: string;
  children: ReactNode;
  /** No padding, for a child that brings its own (a full-bleed list). The header still pads. */
  disablePadding?: boolean;
}

export function SectionCard({
  title,
  headingLevel = 'h2',
  subtitle,
  actions,
  id,
  children,
  disablePadding = false,
}: SectionCardProps) {
  const generatedId = useId();
  const headingId = id ?? generatedId;
  const header = title != null || subtitle != null || actions != null;
  return (
    <Paper
      variant="outlined"
      component="section"
      {...(title != null ? { 'aria-labelledby': headingId } : {})}
      sx={{ p: disablePadding ? 0 : 2 }}
    >
      {header ? (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            mb: 1.5,
            ...(disablePadding ? { p: 2, pb: 0 } : {}),
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
            {title != null ? (
              <Typography id={headingId} variant={headingLevel} component={headingLevel}>
                {title}
              </Typography>
            ) : null}
            {subtitle != null ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: title != null ? 0.5 : 0 }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {actions != null ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
              {actions}
            </Box>
          ) : null}
        </Stack>
      ) : null}
      {children}
    </Paper>
  );
}
